import { t } from '../../shared/i18n.js';
// The phone: a lock screen of notifications and an app grid, and one light screen per app,
// all built from the shared components. The Panels class owns the node; this draws into it.
import { el, clear } from './dom.js';
import { icon } from './icons.js';
import { button, card, row, pill, progress, empty, section, confirm } from './components.js';
import { NEEDS, NEED_NAMES, TRAITS, SKILLS, SKILL_NAMES, DREAMS, HOMES, ITEMS, ACTIONS, CAREERS, PROMOTION_SKILL, BUSINESSES, LAND, VEHICLES_FOR_SALE, LOAN, TRANSFER, ACHIEVEMENTS } from '../../shared/content/life.js';

const APPS = [
  ['me', t('Me'), 'person', 'me'],
  ['jobs', t('Jobs'), 'briefcase', 'jobs'],
  ['do', t('Do'), 'bolt', 'do'],
  ['home', t('Home'), 'home', 'home'],
  ['market', t('Market'), 'basket', 'market'],
  ['money', t('Money'), 'coin', 'money'],
  ['family', t('Family'), 'family', 'family'],
  ['council', t('Council'), 'council', 'council'],
  ['music', t('Music'), 'music', 'music'],
  ['map', t('Map'), 'map', 'map'],
  ['settings', t('Settings'), 'settings', 'settings'],
];
const DOCK = ['me', 'do', 'money', 'music'];
const NEED_ICON = { hunger: 'bowl', energy: 'bolt', fun: 'smile', social: 'family', hygiene: 'drop', bladder: 'toilet' };
const fcfa = (n) => `${Math.round(n).toLocaleString('en-US')} FCFA`;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hh = (h) => `${String(Math.floor(h % 24)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

export class Phone {
  // panels: the Panels instance (node, ask, close). hooks: { onMap, onSetting }
  constructor(panels) {
    this.panels = panels;
    this.app = 'apps';
    this.dismissed = new Set();
    this.busy = false;
  }

  get ask() {
    return this.panels.ask;
  }

  // Draws the phone for an app ('apps' is the lock screen) with the data the game gathered.
  show(app, data) {
    if (this.busy) {
      this.pendingRender = [app, data];
      return;
    }
    const node = this.panels.node;
    const scroll = this.body && this.app === app ? this.body.scrollTop : 0;
    this.app = app;
    this.data = data;
    clear(node);
    node.hidden = false;
    node.className = 'panel phone';
    node.setAttribute('role', 'dialog');
    node.setAttribute('aria-label', 'Phone');
    const meta = APPS.find((a) => a[0] === app);
    const title = app === 'apps' ? '' : meta ? t(meta[1]) : app;
    node.append(this.handle(), this.statusBar(data));
    if (app !== 'apps') node.append(this.top(app, title, data));
    this.body = el('div.phone-body', { class: app === 'apps' ? 'wallpaper' : undefined });
    node.append(this.body, this.homeBar(app));
    this.parkPlayer();
    if (!data.life) {
      this.body.append(empty(t('The phone needs the server. It is not connected.'), 'warning'));
      return;
    }
    const screen = { apps: this.lock, me: this.me, jobs: this.jobs, do: this.do, home: this.home, market: this.market, money: this.money, family: this.family, council: this.council, settings: this.settings, music: this.music }[app] || this.lock;
    screen.call(this, data);
    this.body.scrollTop = scroll;
  }

  // A handle to pull the sheet down on a phone: past 120 px it closes, else it snaps back.
  handle() {
    const h = el('div.sheet-handle', { 'aria-hidden': 'true' });
    const node = this.panels.node;
    let y0 = null;
    h.addEventListener('touchstart', (e) => { y0 = e.touches[0].clientY; node.classList.add('dragging'); }, { passive: true });
    h.addEventListener('touchmove', (e) => {
      if (y0 === null) return;
      const dy = Math.max(0, e.touches[0].clientY - y0);
      node.style.setProperty('--sheet-y', dy + 'px');
    }, { passive: true });
    const end = (e) => {
      if (y0 === null) return;
      const dy = Math.max(0, (e.changedTouches[0] ? e.changedTouches[0].clientY : y0) - y0);
      node.classList.remove('dragging');
      node.style.setProperty('--sheet-y', '0px');
      y0 = null;
      if (dy > 120) this.panels.close();
    };
    h.addEventListener('touchend', end);
    h.addEventListener('touchcancel', end);
    return h;
  }

  // The status bar every phone has: the time, then signal, wifi and battery.
  statusBar(data) {
    const online = !!data.life;
    return el('div.phone-status', null,
      el('span.st-time', null, this.timeText()),
      el('span.st-notch', { 'aria-hidden': 'true' }),
      el('span.st-icons', null,
        el('span.st-signal', { title: online ? 'MTN' : t('No signal') }, el('i'), el('i'), el('i'), el('i', { class: online ? undefined : 'off' })),
        el('span.st-net', null, online ? '4G' : '—'),
        el('span.st-battery', { title: '86%' }, el('i'))
      )
    );
  }

  // The app's head: back, title, balance, close.
  top(app, title, data) {
    const money = data.money === null || data.money === undefined ? '…' : fcfa(data.money);
    const head = el('div.phone-top');
    head.append(button('', { kind: 'ghost', size: 'sm', icon: 'back', ariaLabel: t('Back to the apps'), onclick: () => this.show('apps', this.data) }));
    head.append(el('h2', null, title));
    head.append(pill(money, 'accent'));
    head.append(button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Close the phone'), extra: 'close', onclick: () => this.panels.close() }));
    return head;
  }

  // The bar at the foot: a tap goes home; on the lock screen the dock sits above it.
  homeBar(app) {
    const bar = el('div.phone-foot');
    if (app === 'apps') {
      const dock = el('div.dock-apps');
      for (const id of DOCK) {
        const a = APPS.find((x) => x[0] === id);
        dock.append(el('button.app', { type: 'button', 'data-app': id, 'aria-label': t(a[1]), onclick: () => this.open(id) }, el('span.app-ic', { class: `app-${a[3]}` }, icon(a[2], 24))));
      }
      bar.append(dock);
    }
    bar.append(el('button.home-bar', { type: 'button', 'aria-label': app === 'apps' ? t('Put the phone away') : t('Home'), onclick: () => (app === 'apps' ? this.panels.close() : this.show('apps', this.data)) }));
    return bar;
  }

  dateText() {
    const d = new Date();
    try {
      return d.toLocaleDateString(document.documentElement.lang === 'fr' ? 'fr-FR' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch {
      return '';
    }
  }

  timeText() {
    const h = this.data && this.data.hour !== undefined ? this.data.hour : 12;
    return `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')}`;
  }

  // Asks before money leaves: resolves true when the player agrees.
  async sure(o) {
    this.busy = true;
    const yes = await confirm(this.panels.node, o);
    this.busy = false;
    if (this.pendingRender) {
      const [app, data] = this.pendingRender;
      this.pendingRender = null;
      this.show(app, data);
    }
    return yes;
  }

  // ---- the lock screen: what happened, then the apps
  lock(data) {
    const life = data.life;
    this.body.append(button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Close the phone'), extra: 'close lock-close', onclick: () => this.panels.close() }));
    this.body.append(el('div.lock-clock', null, el('div.lock-time', null, this.timeText()), el('div.lock-date', null, this.dateText())));
    if (this.playing) this.body.append(this.nowPlaying());
    const notes = (data.news || []).filter((n) => !this.dismissed.has(n.date + n.text)).slice(0, 5);
    const nudges = [];
    if (!life.traits.length) nudges.push({ icon: 'person', tone: 'accent', text: t('Pick your two traits and a dream in Me.'), app: 'me' });
    if (data.family && data.family.sex === null) nudges.push({ icon: 'family', tone: 'accent', text: t('Say who you are in Family, once.'), app: 'family' });
    if (!life.career) nudges.push({ icon: 'briefcase', tone: 'info', text: t('No job yet. Apply in Jobs, then work a shift a day.'), app: 'jobs' });
    const low = NEEDS.filter((n) => (data.needs[n] || 0) < 25);
    if (low.length) nudges.push({ icon: NEED_ICON[low[0]], tone: 'danger', text: t('{need} is low. See Do.', { need: t(NEED_NAMES[low[0]]) }), app: 'do' });
    if (notes.length || nudges.length) {
      this.body.append(section(t('Notifications')));
      const list = card([], { padded: false });
      for (const n of nudges) list.append(row({ icon: n.icon, tone: n.tone, title: n.text, onclick: () => this.show(n.app, this.data) }));
      for (const n of notes) {
        list.append(row({ icon: 'bell', title: n.text, meta: n.date, action: button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Dismiss'), onclick: (e) => { e.stopPropagation(); this.dismissed.add(n.date + n.text); this.show('apps', this.data); } }) }));
      }
      this.body.append(list);
    } else this.body.append(empty(t('Nothing new. Greet people and the news of them comes here.'), 'bell'));
    this.body.append(section(t('Apps')));
    const grid = el('div.apps');
    for (const [id, label, ic, tone] of APPS) {
      if (DOCK.includes(id)) continue;
      const badge = this.badge(id, data);
      grid.append(el('button.app', { type: 'button', 'data-app': id, 'aria-label': t(label), onclick: () => this.open(id) }, el('span.app-ic', { class: `app-${tone}` }, icon(ic, 24), badge ? el('span.app-badge', null, badge) : null), el('span.app-label', null, t(label))));
    }
    this.body.append(grid);
  }

  badge(id, data) {
    const life = data.life;
    if (id === 'me' && !life.traits.length) return '!';
    if (id === 'family' && data.family && (data.family.sex === null || (data.family.candidates && data.family.candidates.length))) return data.family.sex === null ? '!' : String(data.family.candidates.length);
    if (id === 'council' && data.election && !data.election.election.voted) return '1';
    if (id === 'jobs' && !life.career) return '!';
    return '';
  }

  open(id) {
    if (id === 'map') {
      this.panels.close();
      if (this.panels.onMap) this.panels.onMap();
      return;
    }
    if (this.panels.onOpenApp) this.panels.onOpenApp(id);
    else this.show(id, this.data);
  }

  // ---- Me: needs, traits and dream, skills, the people you know
  me(data) {
    const life = data.life;
    this.body.append(section(t('Needs')));
    const needs = card([], { padded: false });
    for (const n of NEEDS) {
      const v = Math.round(data.needs[n] || 0);
      const r = row({ icon: NEED_ICON[n], tone: v < 25 ? 'danger' : undefined, title: t(NEED_NAMES[n]), value: `${v}%`, valueTone: v < 25 ? 'danger' : undefined });
      r.querySelector('.row-main').append(progress(v, { label: t(NEED_NAMES[n]) }));
      needs.append(r);
    }
    this.body.append(needs);
    if (!life.traits.length) {
      this.body.append(section(t('Choose two traits, once')), el('p.help', null, t('They shape how fast you learn and what wears you down.')));
      const picked = new Set();
      const list = card([], { padded: false });
      const items = [];
      for (const [id, tr] of Object.entries(TRAITS)) {
        const r = row({ icon: 'star', title: t(tr.name), meta: t(tr.text), extra: 'act', onclick: () => {
          if (picked.has(id)) picked.delete(id);
          else if (picked.size < 2) picked.add(id);
          for (const [bid, bb] of items) {
            bb.classList.toggle('on', picked.has(bid));
            bb.setAttribute('aria-pressed', picked.has(bid) ? 'true' : 'false');
          }
          keep.disabled = picked.size !== 2;
        } });
        r.setAttribute('aria-pressed', 'false');
        items.push([id, r]);
        list.append(r);
      }
      this.body.append(list, section(t('Lifetime dream')));
      const dream = el('select', { 'aria-label': t('Lifetime dream') });
      for (const [id, name] of Object.entries(DREAMS)) dream.append(el('option', { value: id }, t(name)));
      const keep = button(t('Keep these'), { kind: 'primary', disabled: true, extra: 'act', onclick: () => this.ask('traits', { traits: [...picked], dream: dream.value }) });
      this.body.append(el('div.field', null, dream), el('div.actions', null, keep));
    } else {
      this.body.append(section(t('Who you are')));
      this.body.append(card([
        row({ icon: 'star', tone: 'accent', title: t('Traits'), value: life.traits.map((k) => t(TRAITS[k].name)).join(', ') }),
        row({ icon: 'heart', tone: 'accent', title: t('Dream'), value: life.dream ? t(DREAMS[life.dream]) : t('None yet') }),
      ], { padded: false }));
    }
    this.body.append(section(t('Skills')));
    const skills = card([], { padded: false });
    for (const s of SKILLS) {
      const lv = life.skills[s].level;
      const r = row({ title: t(SKILL_NAMES[s]), value: lv ? `${t('level')} ${lv}` : '—' });
      r.querySelector('.row-main').append(progress((lv / 10) * 100, { kind: 'accent', label: t(SKILL_NAMES[s]) }));
      skills.append(r);
    }
    this.body.append(skills);
    this.body.append(section(t('People you know: {n}', { n: data.known || 0 })), el('p.help', null, t('Greet people (E) and they remember you; you hear when life happens to them.')));
    if ((data.news || []).length) this.body.append(card((data.news || []).slice(0, 6).map((n) => row({ icon: 'bell', title: n.text, meta: n.date })), { padded: false }));
  }

  // ---- Do: what you can do here and now
  do(data) {
    this.body.append(el('p.help', null, data.atHome ? t('You are at home in {place}.', { place: data.home.name }) : t('Home is in {place}. Things at home need you there.', { place: data.home.name })));
    const now = Date.now();
    const groups = new Map();
    for (const [id, a] of Object.entries(ACTIONS)) {
      const where = a.at === 'home' ? t('At home') : t('At a {place}', { place: t(a.at === 'bus' ? 'motor park' : a.at) });
      if (!groups.has(where)) groups.set(where, []);
      groups.get(where).push([id, a]);
    }
    for (const [where, list] of groups) {
      this.body.append(section(where));
      const box = card([], { padded: false });
      for (const [id, a] of list) {
        const here = a.at === 'home' ? data.atHome : data.near.includes(a.at);
        const last = data.life.last[id] || 0;
        const wait = Math.max(0, Math.ceil((a.cooldown * 60000 - (now - last)) / 60000));
        const gives = Object.entries(a.gives).map(([n, v]) => `${v > 0 ? '+' : ''}${v} ${t(NEED_NAMES[n]).toLowerCase()}`).join(', ');
        const disabled = !here || wait > 0;
        box.append(row({
          icon: NEED_ICON[Object.keys(a.gives)[0]] || 'bolt',
          title: t(a.name),
          meta: gives,
          value: a.cost ? fcfa(a.cost) : t('Free'),
          disabled,
          reason: !here ? t('Go there first') : t('Ready in {n} min', { n: wait }),
          action: button(t('Do'), { kind: 'primary', size: 'sm', extra: 'act', disabled, onclick: () => this.ask('act', { what: id, hour: data.hour, weekday: data.weekday, skill: 'hustle' }) }),
        }));
      }
      this.body.append(box);
    }
  }

  // ---- Home: where you live, your things, a taxi home, moving
  home(data) {
    const life = data.life;
    const h = HOMES[life.home];
    this.body.append(card([el('div.big-title', null, t(h.name)), el('div.help', null, `${h.where} · ${fcfa(h.rent)} ${t('a week, taken every Saturday')}`)]));
    this.body.append(section(t('Your things')));
    this.body.append(life.items.length ? el('div.pills', null, life.items.map((i) => pill(t(ITEMS[i].name)))) : el('p.help', null, t('Nothing yet. The Market sells things for the home.')));
    if (!data.atHome) this.body.append(el('div.actions', null, button(t('Taxi home to {place}', { place: data.home.name }), { kind: 'primary', icon: 'taxi', extra: 'act', onclick: () => this.ask('gohome', { hour: data.hour }) })), el('p.help', null, t('The fare of the distance.')));
    this.body.append(section(t('Move house')), el('p.help', null, t('Three weeks of the new rent up front.')));
    const list = card([], { padded: false });
    for (const [id, h2] of Object.entries(HOMES)) {
      const mine = id === life.home;
      list.append(row({ icon: 'home', tone: mine ? 'accent' : undefined, title: t(h2.name), meta: `${h2.where}${h2.furnished ? '' : ' · ' + t('unfurnished')}`, value: mine ? t('Yours') : `${fcfa(h2.rent)}/${t('wk')}`, disabled: mine, action: mine ? null : button(t('Move'), { size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: t('Move to a {home} in {where}?', { home: t(h2.name).toLowerCase(), where: h2.where }), text: t('Three weeks of rent up front; your things come with you.'), cost: fcfa(h2.rent * 3), okLabel: t('Move') })) this.ask('move', { home: id }); } }) }));
    }
    this.body.append(list);
  }

  // ---- Jobs
  jobs(data) {
    const life = data.life;
    if (life.career) {
      const c = CAREERS[life.career.id];
      const k = life.career;
      const need = PROMOTION_SKILL[k.level];
      this.body.append(card([
        el('div.big-title', null, `${c.titles[k.level - 1]}, ${c.name}`),
        el('div.help', null, `At ${k.place.n} · ${hh(c.shift.start)}–${hh(c.shift.end)} ${c.shift.days.map((d) => DAYS[d]).join(' ')}`),
        el('div.pills', null, pill(`${fcfa(c.pay[k.level - 1])} a shift`, 'accent'), pill(`${k.shifts} shift${k.shifts === 1 ? '' : 's'}`), pill(`performance ${k.performance}%`, k.performance >= 80 ? 'success' : 'warning')),
        need ? el('div.help', null, `Next level needs ${SKILL_NAMES[c.skill]} ${need} (yours: ${life.skills[c.skill].level}).`) : null,
        need ? progress((life.skills[c.skill].level / need) * 100, { kind: 'accent', label: t('Towards the next level') }) : null,
        el('div.help', null, t('Go there in the shift hours and press E. One shift a day; late costs performance.')),
        el('div.actions', null, button(t('Leave this job'), { kind: 'danger', size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: `Leave ${c.name}?`, text: t('You can apply again later; the level is lost.'), okLabel: t('Leave'), danger: true })) this.ask('quitjob', {}); } })),
      ]));
      this.body.append(section(t('Other work in town')));
    } else this.body.append(section(t('Work in town')), el('p.help', null, t('Apply, then go there in the shift hours and press E. The nearest workplace of the kind to your home.')));
    const list = card([], { padded: false });
    for (const [id, c] of Object.entries(CAREERS)) {
      if (life.career && life.career.id === id) continue;
      list.append(row({ icon: 'briefcase', title: c.name, meta: `${SKILL_NAMES[c.skill]} · ${hh(c.shift.start)}–${hh(c.shift.end)} ${c.shift.days.map((d) => DAYS[d]).join(' ')}`, value: `${(c.pay[0] / 1000).toFixed(0)}–${(c.pay[4] / 1000).toFixed(0)}k`, action: button(t('Apply'), { size: 'sm', extra: 'act', onclick: () => this.ask('apply', { career: id }) }) }));
    }
    this.body.append(list);
  }

  // ---- Market
  market(data) {
    const life = data.life;
    this.body.append(section(t('Vehicles')), el('p.help', null, t('Yours waits outside your home each morning.')));
    const vehicles = card([], { padded: false });
    for (const [id, v] of Object.entries(VEHICLES_FOR_SALE)) {
      const have = (life.vehicles || []).some((x) => x.id === id);
      vehicles.append(row({ icon: v.type === 'bike' ? 'bolt' : v.type === 'bus' ? 'bus' : 'car', tone: have ? 'success' : undefined, title: v.name, value: have ? t('Yours') : fcfa(v.price), disabled: have, action: have ? null : this.buyButton(`Buy a ${v.name.toLowerCase()}?`, v.price, () => this.ask('buyasset', { kind: 'vehicle', id })) }));
    }
    this.body.append(vehicles, section(t('Businesses')), el('p.help', null, t('They pay every midnight without a shift; some days go badly.')));
    const biz = card([], { padded: false });
    for (const [id, b] of Object.entries(BUSINESSES)) {
      const own = (life.businesses || []).find((x) => x.id === id);
      biz.append(row({ icon: 'basket', tone: own ? 'success' : undefined, title: b.name, meta: `${b.where} · ${fcfa(b.pays[0])} to ${fcfa(b.pays[1])} a day${b.weekends ? ' (weekends)' : ''}${own ? ` · earned ${fcfa(own.earned || 0)}` : ''}`, value: own ? t('Yours') : fcfa(b.price), disabled: !!own, action: own ? null : this.buyButton(`Buy ${b.name}?`, b.price, () => this.ask('buyasset', { kind: 'business', id })) }));
    }
    this.body.append(biz, section(t('Land')), el('p.help', null, t('Grows in value every week; the quarter head takes a levy when you buy.')));
    const land = card([], { padded: false });
    for (const [id, l] of Object.entries(LAND)) {
      const own = (life.land || []).find((x) => x.id === id);
      const price = Math.round(l.price * (1 + l.levy));
      if (own) land.append(row({ icon: 'pin', tone: 'success', title: l.name, meta: `Worth ${fcfa(own.value)} now`, value: t('Yours'), action: button(t('Sell'), { size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: `Sell ${l.name}?`, text: `The buyer pays ${fcfa(own.value)}.`, okLabel: t('Sell') })) this.ask('sellland', { id }); } }) }));
      else land.append(row({ icon: 'pin', title: l.name, meta: `Grows ${Math.round(l.grows * 100)}% a week`, value: fcfa(price), action: this.buyButton(`Buy ${l.name}?`, price, () => this.ask('buyasset', { kind: 'land', id }), t('With the quarter head\'s levy.')) }));
    }
    this.body.append(land, section(t('For the home')));
    const things = card([], { padded: false });
    for (const [id, it] of Object.entries(ITEMS)) {
      const have = life.items.includes(id);
      things.append(row({ icon: 'home', tone: have ? 'success' : undefined, title: it.name, meta: it.text, value: have ? t('Yours') : fcfa(it.price), disabled: have, action: have ? null : this.buyButton(`Buy a ${it.name.toLowerCase()}?`, it.price, () => this.ask('buy', { item: id })) }));
    }
    this.body.append(things);
  }

  buyButton(title, price, go, text) {
    return button(t('Buy'), { kind: 'primary', size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title, text, cost: fcfa(price), okLabel: t('Buy') })) go(); } });
  }

  // ---- Money
  money(data) {
    const life = data.life;
    this.body.append(section(t('This week')));
    const week = card([], { padded: false });
    week.append(row({ icon: 'home', title: t('Rent on Saturday'), value: fcfa(data.rent) }));
    if (data.start === 'bushfaller') week.append(row({ icon: 'coin', tone: 'success', title: t('From your people'), meta: t('Every Saturday'), value: '+10,000 FCFA', valueTone: 'success' }));
    if (data.njangi) week.append(row({ icon: 'family', title: t('Njangi'), meta: t('The pot of 20,000 comes round in turn'), value: '2,500 FCFA' }));
    if (life.debt) week.append(row({ icon: 'warning', tone: 'danger', title: t('Owed'), meta: t('Paid from the next earnings'), value: fcfa(life.debt), valueTone: 'danger' }));
    if (life.loan) week.append(row({ icon: 'money', title: t('Loan left'), meta: `${fcfa(life.loan.weekly)} every Saturday`, value: fcfa(life.loan.left) }));
    this.body.append(week);
    if (!life.loan) {
      this.body.append(section(LOAN.name));
      this.body.append(card([row({ icon: 'money', tone: 'info', title: `Borrow ${fcfa(LOAN.amount)}`, meta: `Repay ${fcfa(LOAN.weekly)} every Saturday for ${LOAN.weeks} weeks (${fcfa(LOAN.weekly * LOAN.weeks)} in all)`, action: button(t('Take'), { size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: `Take ${fcfa(LOAN.amount)}?`, text: `${fcfa(LOAN.weekly)} leaves every Saturday for ${LOAN.weeks} weeks. Miss it and your njangi seat goes.`, cost: `${fcfa(LOAN.weekly * LOAN.weeks)} to repay`, okLabel: t('Take the loan'), danger: true })) this.ask('loan', {}); } }) })], { padded: false }));
    }
    this.body.append(section(t('Send money')), el('p.help', null, `${TRANSFER.fee * 100}% fee, at most ${TRANSFER.feeCap} FCFA; ${fcfa(TRANSFER.dailyCap)} a day.`));
    const to = el('input', { type: 'text', placeholder: t('Player name'), maxlength: 20, 'aria-label': t('Player name'), id: 'sendto' });
    const amount = el('input', { type: 'number', placeholder: t('Amount'), min: 100, step: 100, inputmode: 'numeric', 'aria-label': t('Amount in FCFA'), id: 'sendamount' });
    const note = el('p.help.error-text', { hidden: true });
    const send = button(t('Send'), { kind: 'primary', extra: 'act', onclick: async () => {
      const n = Number(amount.value);
      const who = to.value.trim();
      if (!who || !(n >= 100)) {
        note.textContent = t('A name and at least 100 FCFA.');
        note.hidden = false;
        return;
      }
      const fee = Math.min(TRANSFER.feeCap, Math.round(n * TRANSFER.fee));
      if (await this.sure({ title: `Send ${fcfa(n)} to ${who}?`, text: `The fee is ${fcfa(fee)}.`, cost: fcfa(n + fee), okLabel: t('Send') })) this.ask('sendmoney', { to: who, amount: n });
    } });
    this.body.append(card([el('label.help', { for: 'sendto' }, t('To')), to, el('label.help', { for: 'sendamount' }, t('Amount')), amount, note, el('div.actions', null, send)]));
  }

  // ---- Family
  family(data) {
    const f = data.family;
    if (!f) {
      this.body.append(empty(t('Asking the family…'), 'family'));
      return;
    }
    if (f.sex === null) {
      this.body.append(section(t('Who are you?')), el('p.help', null, t('Said once. It decides whom you can marry and who inherits.')));
      this.body.append(el('div.choices', null, button(t('A man'), { kind: 'secondary', size: 'lg', extra: 'act', onclick: () => this.ask('sex', { sex: 1 }) }), button(t('A woman'), { kind: 'secondary', size: 'lg', extra: 'act', onclick: () => this.ask('sex', { sex: 0 }) })));
      return;
    }
    this.body.append(card([el('div.big-title', null, f.self ? f.self.name : t('You')), el('div.help', null, `${t(f.livedDays === 1 ? 'Lived here {n} real day' : 'Lived here {n} real days', { n: f.livedDays })}. ${t('You do not age; your children do, a year a day, and the town with them.')}`)]));
    const kin = card([], { padded: false });
    if (f.mother || f.father) {
      kin.append(row({ icon: 'person', title: t('Mother'), value: f.mother ? `${f.mother.name}${f.mother.alive ? `, ${f.mother.age}` : ` (${t('late')})`}` : t('Unknown') }));
      kin.append(row({ icon: 'person', title: t('Father'), value: f.father ? `${f.father.name}${f.father.alive ? `, ${f.father.age}` : ` (${t('late')})`}` : t('Unknown') }));
    }
    if (f.siblings && f.siblings.length) kin.append(row({ icon: 'family', title: t('Brothers and sisters'), meta: f.siblings.map((s) => `${s.name} (${s.age})`).join(', ') }));
    kin.append(row({ icon: 'heart', tone: f.spouse ? 'accent' : undefined, title: f.sex === 1 ? t('Wife') : t('Husband'), value: f.spouse ? `${f.spouse.name}, ${f.spouse.age}` : t('None'), action: f.spouse ? button(t('Separate'), { kind: 'danger', size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: t('Separate from {who}?', { who: f.spouse.name }), text: t('She goes back to her people; the children mostly stay.'), okLabel: t('Separate'), danger: true })) this.ask('divorce', {}); } }) : null }));
    if (f.wife2) kin.append(row({ icon: 'heart', title: t('Second wife'), value: `${f.wife2.name}, ${f.wife2.age}` }));
    kin.append(row({ icon: 'key', title: t('Heir'), value: f.heir ? `${f.heir.name}, ${f.heir.age}` : t('Not named') }));
    if (f.fees) kin.append(row({ icon: 'school', title: t('School fees'), meta: t('Every Saturday'), value: fcfa(f.fees) }));
    this.body.append(section(t('Your people')), kin);
    this.body.append(section(t('Children ({n})', { n: f.children.length })));
    if (!f.children.length) this.body.append(el('p.help', null, f.spouse ? t('Children come in their own time.') : t('Marry first; or take in a kin child the town sends your way.')));
    else {
      const kids = card([], { padded: false });
      for (const c of f.children) {
        const canName = c.alive && c.age >= 16 && (!f.heir || f.heir.id !== c.id);
        kids.append(row({ icon: c.age < 18 ? 'school' : 'person', title: c.name, meta: `${c.age} · ${c.alive ? t(c.role).toLowerCase() : t('died')}${c.fostered ? ` · ${t('taken in')}` : ''}`, value: f.heir && f.heir.id === c.id ? t('Heir') : '', action: canName ? button(t('Name heir'), { size: 'sm', extra: 'act', onclick: () => this.ask('successor', { id: c.id }) }) : null }));
      }
      this.body.append(kids);
    }
    if (f.heir) {
      this.body.append(section(t('Hand over')));
      this.body.append(card([
        el('div.help', null, t('Go on as {heir}: the house and the name pass whole, the money less {cost} for the celebration, skills capped at level 3, the job given up.', { heir: f.heir.name, cost: fcfa(f.handoverCost) })),
        el('div.actions', null, button(f.canHandOver ? t('Hand over to {heir}', { heir: f.heir.name }) : f.heir.age < 18 ? t('Wait: {heir} is {age}', { heir: f.heir.name, age: f.heir.age }) : t('Wait: live a real week first'), { kind: 'primary', extra: 'act', disabled: !f.canHandOver, onclick: async () => { if (await this.sure({ title: t('Hand over to {heir}?', { heir: f.heir.name }), text: t('Your character becomes a townsperson and you go on as the heir.'), cost: fcfa(f.handoverCost), okLabel: t('Hand over'), danger: true })) this.ask('handover', {}); } })),
      ]));
    }
    if (!(f.sex === 0 && f.spouse) && !f.wife2) {
      this.body.append(section(f.spouse ? t('A second wife') : t('Marry')), el('p.help', null, t('{price}: the bride price list and the wedding. Only people you have greeted.', { price: fcfa(f.bridePrice) })));
      if (!f.candidates.length) this.body.append(empty(t('Nobody yet: greet single people your age (E) and come back.'), 'heart'));
      else {
        const list = card([], { padded: false });
        for (const c of f.candidates) list.append(row({ icon: 'person', title: c.name, meta: `${c.age} · ${t(c.role).toLowerCase()} · ${t('met at {where}', { where: c.where })}`, action: button(t('Propose'), { kind: 'primary', size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: t('Propose to {who}?', { who: c.name }), text: t('The bride price list and the wedding, paid to her people.'), cost: fcfa(f.bridePrice), okLabel: t('Propose') })) this.ask('propose', { id: c.id }); } }) }));
        this.body.append(list);
      }
    }
  }

  // ---- Council
  council(data) {
    const e = data.election;
    if (!e) {
      this.body.append(empty(t('Asking the council…'), 'council'));
      return;
    }
    if (e.mayor) this.body.append(card([el('div.help', null, t('Mayor')), el('div.big-title', null, e.mayor.name), el('div.help', null, `${t(e.policies[e.mayor.policy].name)}: ${t(e.policies[e.mayor.policy].text)} ${t('Until {date}.', { date: e.mayor.until })}`)]));
    else this.body.append(card([el('div.big-title', null, t('No mayor yet')), el('div.help', null, t('The first election is running.'))]));
    this.body.append(section(t('Election closes {date}', { date: e.election.ends })), el('p.help', null, t('Votes are cast at {place}; one each.', { place: e.council ? e.council.n : t('the council') })));
    const cands = card([], { padded: false });
    for (const c of e.election.candidates) {
      cands.append(row({ icon: 'person', tone: c.you ? 'accent' : undefined, title: `${c.name}${c.you ? ` ${t('(you)')}` : ''}`, meta: t(e.policies[c.policy].name), value: t(c.votes === 1 ? '{n} vote' : '{n} votes', { n: c.votes }), action: e.election.voted ? null : button(t('Vote'), { size: 'sm', extra: 'act', onclick: () => this.ask('vote', { name: c.name }) }) }));
    }
    if (e.election.voted) cands.append(row({ icon: 'check', tone: 'success', title: t('You have voted') }));
    this.body.append(cands);
    if (!e.election.candidates.some((c) => c.you)) {
      this.body.append(section(t('Stand for mayor')), el('p.help', null, t('Pick the policy you would hold for a term.')));
      const list = card([], { padded: false });
      for (const [id, pol] of Object.entries(e.policies)) list.append(row({ icon: 'council', title: t(pol.name), meta: t(pol.text), action: button(t('Stand'), { size: 'sm', extra: 'act', onclick: async () => { if (await this.sure({ title: t('Stand on "{policy}"?', { policy: t(pol.name) }), text: t('Your name goes on the ballot until the election closes.'), okLabel: t('Stand') })) this.ask('stand', { policy: id }); } }) }));
      this.body.append(list);
    }
    const got = data.life.achievements || [];
    this.body.append(section(t('Achievements ({n} of {total})', { n: got.length, total: Object.keys(ACHIEVEMENTS).length })));
    const list = card([], { padded: false });
    for (const [id, a] of Object.entries(ACHIEVEMENTS)) list.append(row({ icon: 'star', tone: got.includes(id) ? 'accent' : undefined, title: t(a.name), meta: t(a.text), value: got.includes(id) ? fcfa(a.reward) : '', valueTone: got.includes(id) ? 'success' : undefined }));
    this.body.append(list);
  }

  // ---- Music: YouTube's own player inside the phone. Nothing is hosted here; a video or a
  // playlist plays through YouTube under YouTube's terms, and keeps playing while you walk.
  music() {
    if (!this.musicList) this.loadStations();
    this.body.append(this.playerSlot());
    const link = el('input', { type: 'url', placeholder: t('Paste a YouTube link'), 'aria-label': t('YouTube link'), id: 'ytlink', inputmode: 'url' });
    const go = button(t('Play'), { kind: 'primary', icon: 'music', onclick: () => this.playLink(link.value) });
    link.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.playLink(link.value);
    });
    this.body.append(section(t('Play a song or a playlist')), card([link, el('div.actions', null, go, button(t('Search on YouTube'), { kind: 'ghost', onclick: () => window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(link.value || 'afrobeats')}`, '_blank', 'noopener') }))]), el('p.help', null, t('Any video or playlist link. A video whose label has switched off embedding says so; pick another.')));
    const stations = (this.musicList || []).filter((s) => s.id);
    if (stations.length) {
      this.body.append(section(t('Stations')));
      this.body.append(card(stations.map((s) => row({ icon: 'music', tone: 'accent', title: s.name, meta: s.by || '', action: button(t('Play'), { size: 'sm', onclick: () => this.play({ id: s.id, kind: s.kind || 'playlist', name: s.name }) }) })), { padded: false }));
    } else this.body.append(section(t('Stations')), el('p.help', null, t('The owner can list playlists in public/music.json; they appear here for everyone.')));
    const recent = this.recent();
    if (recent.length) {
      this.body.append(section(t('Recent')));
      this.body.append(card(recent.map((r) => row({ icon: 'music', title: r.name || r.id, meta: r.kind === 'playlist' ? t('Playlist') : t('Video'), action: button(t('Play'), { size: 'sm', onclick: () => this.play(r) }) })), { padded: false }));
    }
  }

  loadStations() {
    this.musicList = [];
    fetch('/music.json').then((r) => (r.ok ? r.json() : [])).then((list) => {
      this.musicList = Array.isArray(list) ? list : [];
      if (this.app === 'music') this.show('music', this.data);
    }).catch(() => {});
  }

  recent() {
    try {
      return JSON.parse(localStorage.getItem('abakwa-music') || '[]');
    } catch {
      return [];
    }
  }

  // A link or a bare id: a video id, or a playlist id (list=...).
  playLink(text) {
    const s = String(text || '').trim();
    if (!s) return;
    let m = s.match(/[?&]list=([A-Za-z0-9_-]+)/);
    if (m) return this.play({ id: m[1], kind: 'playlist', name: t('Playlist') });
    m = s.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([A-Za-z0-9_-]{11})/) || s.match(/^([A-Za-z0-9_-]{11})$/);
    if (m) return this.play({ id: m[1], kind: 'video', name: t('Video') });
    if (/^PL[A-Za-z0-9_-]{10,}$|^RD[A-Za-z0-9_-]{10,}$|^OL[A-Za-z0-9_-]{10,}$/.test(s)) return this.play({ id: s, kind: 'playlist', name: t('Playlist') });
    this.panels.node.querySelector('#ytlink') && this.panels.node.querySelector('#ytlink').classList.add('bad');
  }

  play(item) {
    this.playing = item;
    const src = item.kind === 'playlist' ? `https://www.youtube.com/embed/videoseries?list=${encodeURIComponent(item.id)}&autoplay=1&playsinline=1&rel=0` : `https://www.youtube.com/embed/${encodeURIComponent(item.id)}?autoplay=1&playsinline=1&rel=0`;
    const host = this.playerHost();
    clear(host);
    host.append(el('iframe', { src, title: 'YouTube', allow: 'autoplay; encrypted-media; picture-in-picture', allowfullscreen: true, referrerpolicy: 'strict-origin-when-cross-origin' }));
    try {
      const list = this.recent().filter((r) => r.id !== item.id);
      list.unshift({ id: item.id, kind: item.kind, name: item.name });
      localStorage.setItem('abakwa-music', JSON.stringify(list.slice(0, 8)));
    } catch {
      // not remembered
    }
    if (this.app === 'music') this.show('music', this.data);
  }

  stop() {
    this.playing = null;
    const host = this.playerHost();
    clear(host);
    this.show(this.app, this.data);
  }

  // The one element that holds the player: it moves into the Music screen and parks off screen
  // otherwise, so the music never stops when you leave the app or close the phone.
  playerHost() {
    if (!this._host) {
      this._host = el('div.yt-host');
      this._park = el('div', { id: 'ytpark', 'aria-hidden': 'true' }, this._host);
      document.body.append(this._park);
    }
    return this._host;
  }

  parkPlayer() {
    if (this._host && this._host.parentNode !== this._park) this._park.append(this._host);
  }

  playerSlot() {
    const slot = el('div.player-slot');
    const host = this.playerHost();
    if (this.playing) {
      slot.append(host);
      slot.append(el('div.player-bar', null, el('span.player-name', null, this.playing.name || this.playing.id), button(t('Stop'), { kind: 'ghost', size: 'sm', icon: 'close', onclick: () => this.stop() })));
    } else slot.append(empty(t('Nothing playing. Paste a link below.'), 'music'));
    return slot;
  }

  nowPlaying() {
    return el('button.now-playing', { type: 'button', onclick: () => this.show('music', this.data) }, icon('music', 18), el('span', null, `${t('Playing')} · ${this.playing.name || this.playing.id}`), icon('chevron', 16));
  }

  // ---- Settings
  settings(data) {
    const s = this.panels.settings || {};
    const list = card([], { padded: false });
    list.append(row({ icon: 'clock', title: t('Day and night'), meta: s.clock === 'real' ? t('Real Bamenda time') : t('A whole day every two hours'), action: button(s.clock === 'real' ? t('Faster') : t('Real time'), { size: 'sm', onclick: () => this.panels.onSetting && this.panels.onSetting('clock') }) }));
    list.append(row({ icon: 'bell', title: t('Sound'), meta: s.sound === false ? t('Off') : t('On'), action: button(s.sound === false ? t('Turn on') : t('Turn off'), { size: 'sm', onclick: () => this.panels.onSetting && this.panels.onSetting('sound') }) }));
    list.append(row({ icon: 'settings', title: t('Quality'), meta: `${s.quality || 'medium'} · ${t('changes on reload')}`, action: button(t('Change'), { size: 'sm', onclick: () => this.panels.onSetting && this.panels.onSetting('quality') }) }));
    list.append(row({ icon: 'person', title: t('Name and look'), meta: t('Back to the first screen'), action: button(t('Change'), { size: 'sm', onclick: () => this.panels.onSetting && this.panels.onSetting('profile') }) }));
    list.append(row({ icon: 'chat', title: t('Language'), meta: s.language || 'English', action: button(t('Change'), { size: 'sm', onclick: () => this.panels.onSetting && this.panels.onSetting('language') }) }));
    this.body.append(section(t('Settings')), list);
    this.body.append(section(t('About')), card([el('div.help', null, t('Abakwa Mythlands. Map data © OpenStreetMap contributors. Everything in the town is fiction.')), el('div.actions', null, el('a', { href: '/rules.html', target: '_blank', rel: 'noopener' }, t('The rules of the town')))]));
  }
}
